// src/components/Common/ProductDescription.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Grid,
  FormHelperText,
  Box,
  IconButton,
  Toolbar,
  Paper,
  Popover,
  ButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
  Divider,
  Chip,
  Typography,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatListBulleted,
  FormatListNumbered,
  EmojiEmotions,
  Undo,
  Redo,
  FormatClear,
  Palette,
  Star,
  LocalOffer,
  Straighten,
  AutoAwesome,
  CheckCircle,
} from '@mui/icons-material';

const MAX_DESCRIPTION_LENGTH = 2000;

// Enhanced emoji categories for clothing store - Arabic labels
const EMOJI_CATEGORIES = {
  'الملابس': ['👕', '👖', '👗', '👔', '👚', '👙', '🩱', '👘', '🥻', '🩳', '👰‍♀️', '🤵‍♂️', '👯‍♀️', '🧥', '🥼', '🦺', '👞', '👠', '🥿', '👢', '👡', '🩴', '👟', '🥾'],
  'الإكسسوارات': ['👒', '🧢', '⛑️', '🎩', '👑', '💍', '💎', '📿', '💄', '👝', '👛', '👜', '🎒', '👓', '🕶️', '🥽', '⌚', '🧳', '🧤', '🧣', '👔', '🎀', '🌂', '☂️'],
  'الموضة': ['✨', '💫', '⭐', '🌟', '💎', '👸', '🤴', '💅', '💄', '👗', '🎭', '🎪', '🎨', '🏆', '🥇', '🎯', '💯', '🔥', '❤️', '💖', '💝', '🎁', '🛍️', '🏷️'],
  'المواسم': ['☀️', '🌸', '🍂', '❄️', '🌺', '🌻', '🌷', '🌹', '🍃', '🌿', '🌱', '🌲', '🎄', '🏖️', '⛱️', '🏔️', '🌊', '🌈', '☁️', '⛅', '🌤️', '⚡', '🌙', '⭐'],
  'الجودة': ['✅', '💯', '🏆', '🥇', '⭐', '🌟', '💎', '👌', '✨', '🔥', '💪', '👍', '🎯', '📏', '📐', '🧵', '✂️', '🪡', '🧶', '🔖', '🏷️', '💳', '💰', '💸'],
  'المناسبات': ['🎉', '🎊', '🥳', '🎈', '🎁', '💒', '💍', '👰', '🤵', '🎂', '🍾', '🥂', '🌹', '💐', '🎭', '🎪', '🎨', '🎼', '🎵', '💃', '🕺', '🏃‍♀️', '🏃‍♂️', '🚶‍♀️']
};

// Clothing-specific text templates - Arabic content
const QUICK_TEMPLATES = [
  { label: 'المادة', text: '100% قطن' },
  //{ label: 'العناية', text: 'يُغسل بالماء البارد في الغسالة' },
  { label: 'المقاس', text: 'مقاس عادي' },
  { label: 'دليل المقاسات', text: 'راجع جدول المقاسات' },
  { label: 'المميزات', text: '• قماش قابل للتنفس\n• مقاس مريح\n• تصميم متين' },
  { label: 'الموسم', text: 'مثالي للربيع والصيف' },
  { label: 'الألوان المتاحة', text: 'متوفر بألوان متعددة' },
  { label: 'التوصيل', text: 'توصيل مجاني للطلبات فوق 200 ريال' },
];

// Color palette for text highlighting
const COLOR_PALETTE = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', 
  '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43'
];

const ProductDescription = ({ 
  value = '', 
  onChange, 
  rtlTextFieldProps = {},
  maxLength = MAX_DESCRIPTION_LENGTH 
}) => {
  const [editorContent, setEditorContent] = useState('');
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [templateAnchorEl, setTemplateAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('الملابس');
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const editorRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helper function to set cursor position at the end
  const setCursorAtEnd = () => {
    if (editorRef.current) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // Collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Helper function to get current cursor position
  const getCurrentCursorPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      return {
        startContainer: range.startContainer,
        startOffset: range.startOffset,
        endContainer: range.endContainer,
        endOffset: range.endOffset
      };
    }
    return null;
  };

  // Helper function to restore cursor position
  // Replace the existing restoreCursorPosition function with this
const restoreCursorPosition = useCallback((position) => {
  if (position && editorRef.current) {
    try {
      const range = document.createRange();
      const selection = window.getSelection();
      
      if (editorRef.current.contains(position.startContainer) && 
          editorRef.current.contains(position.endContainer)) {
        range.setStart(position.startContainer, position.startOffset);
        range.setEnd(position.endContainer, position.endOffset);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        setCursorAtEnd();
      }
    } catch (error) {
      console.warn('Could not restore cursor position:', error);
      setCursorAtEnd();
    }
  }
}, []);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && !isInitialized) {
      editorRef.current.innerHTML = value || '';
      setEditorContent(value || '');
      setIsInitialized(true);
      // Set initial cursor position
      if (value) {
        setTimeout(() => setCursorAtEnd(), 0);
      }
    }
  }, [value, isInitialized]);

  // Update content when value prop changes
  useEffect(() => {
    if (isInitialized && editorRef.current && editorRef.current.innerHTML !== value) {
      const cursorPosition = getCurrentCursorPosition();
      editorRef.current.innerHTML = value || '';
      setEditorContent(value || '');
      // Restore cursor position after content update
      setTimeout(() => {
        if (cursorPosition) {
          restoreCursorPosition(cursorPosition);
        } else {
          setCursorAtEnd();
        }
      }, 0);
    }
  }, [value, isInitialized, restoreCursorPosition]);

  // Save state for undo/redo
  const saveState = () => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML;
      setUndoStack(prev => [...prev.slice(-9), currentContent]);
      setRedoStack([]);
    }
  };

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setRedoStack(prev => [editorContent, ...prev]);
      setUndoStack(prev => prev.slice(0, -1));
      if (editorRef.current) {
        editorRef.current.innerHTML = previousState;
        setEditorContent(previousState);
        onChange(previousState);
        setTimeout(() => setCursorAtEnd(), 0);
      }
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[0];
      setUndoStack(prev => [...prev, editorContent]);
      setRedoStack(prev => prev.slice(1));
      if (editorRef.current) {
        editorRef.current.innerHTML = nextState;
        setEditorContent(nextState);
        onChange(nextState);
        setTimeout(() => setCursorAtEnd(), 0);
      }
    }
  };

  const executeCommand = (command, value = null) => {
    const cursorPosition = getCurrentCursorPosition();
    saveState();
    
    // Focus the editor first
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    document.execCommand(command, false, value);
    handleContentChange();
    
    // Restore cursor position after command execution
    setTimeout(() => {
      if (cursorPosition) {
        restoreCursorPosition(cursorPosition);
      }
    }, 0);
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      const textContent = editorRef.current.textContent || '';
      
      if (textContent.length <= maxLength) {
        setEditorContent(content);
        onChange(content);
      } else {
        // Restore previous content if exceeds max length
        editorRef.current.innerHTML = editorContent;
        setTimeout(() => setCursorAtEnd(), 0);
      }
    }
  };

  const insertEmoji = (emoji) => {
    saveState();
    
    if (editorRef.current) {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const emojiNode = document.createTextNode(emoji);
        range.insertNode(emojiNode);
        
        // Move cursor after the emoji
        range.setStartAfter(emojiNode);
        range.setEndAfter(emojiNode);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append at the end
        editorRef.current.innerHTML += emoji;
        setCursorAtEnd();
      }
    }
    
    handleContentChange();
    setEmojiAnchorEl(null);
  };

  const insertTemplate = (templateText) => {
    saveState();
    
    if (editorRef.current) {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        const lines = templateText.split('\n');
        let lastNode = null;
        
        lines.forEach((line, index) => {
          if (index > 0) {
            const br = document.createElement('br');
            range.insertNode(br);
            lastNode = br;
          }
          const textNode = document.createTextNode(line);
          range.insertNode(textNode);
          lastNode = textNode;
        });
        
        // Move cursor to end of inserted content
        if (lastNode) {
          range.setStartAfter(lastNode);
          range.setEndAfter(lastNode);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        // If no selection, append at the end
        editorRef.current.innerHTML += templateText.replace(/\n/g, '<br>');
        setCursorAtEnd();
      }
    }
    
    handleContentChange();
    setTemplateAnchorEl(null);
  };

  const applyColor = (color) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      saveState();
      executeCommand('foreColor', color);
    }
    setColorAnchorEl(null);
  };

  const highlightText = (color) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      saveState();
      executeCommand('hiliteColor', color);
    }
    setColorAnchorEl(null);
  };

  const insertSpecialFormat = (type) => {
    saveState();
    let htmlContent = '';
    
    switch(type) {
      case 'size-chart':
        htmlContent = '<div style="border: 1px solid #ddd; padding: 10px; margin: 5px 0; background: #f9f9f9;">📏 <strong>جدول المقاسات متاح</strong> - راجع دليل المقاسات للحصول على المقاس المثالي</div>';
        break;
      case 'care-instructions':
        htmlContent = '<div style="border-right: 3px solid #4ECDC4; padding: 10px; margin: 5px 0; background: #f0fdfc;">🧺 <strong>تعليمات العناية:</strong><br>• يُغسل بالماء البارد<br>• يُجفف على حرارة منخفضة<br>• لا يُستخدم المُبيض</div>';
        break;
      case 'features-list':
        htmlContent = '<div style="background: linear-gradient(90deg, #FF6B6B, #4ECDC4); color: white; padding: 10px; margin: 5px 0; border-radius: 5px;">✨ <strong>المميزات الرئيسية:</strong><br>• مادة عالية الجودة<br>• مقاس مريح<br>• متانة طويلة الأمد</div>';
        break;
      case 'promotion':
        htmlContent = '<div style="border: 2px dashed #FF6B6B; padding: 10px; margin: 5px 0; background: #ffe6e6; text-align: center;">🏷️ <strong>عرض خاص!</strong> عرض لفترة محدودة - لا تفوت الفرصة!</div>';
        break;
      case 'warranty':
        htmlContent = '<div style="border: 1px solid #4CAF50; padding: 10px; margin: 5px 0; background: #f1f8e9;">🛡️ <strong>ضمان الجودة:</strong> ضمان استبدال أو استرداد لمدة 30 يوم</div>';
        break;
      case 'shipping':
        htmlContent = '<div style="border: 1px solid #2196F3; padding: 10px; margin: 5px 0; background: #e3f2fd;">🚚 <strong>التوصيل:</strong> توصيل مجاني للطلبات فوق 200 ريال - التوصيل خلال 2-3 أيام عمل</div>';
        break;
      default:
        console.warn(`Unknown special format type: ${type}`);
        break;
    }
    
    if (editorRef.current && htmlContent) {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const div = document.createElement('div');
        div.innerHTML = htmlContent;
        const fragment = div.firstChild;
        range.insertNode(fragment);
        
        // Move cursor after the inserted element
        range.setStartAfter(fragment);
        range.setEndAfter(fragment);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current.innerHTML += htmlContent;
        setCursorAtEnd();
      }
      handleContentChange();
    }
  };

  const clearFormatting = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      saveState();
      const range = selection.getRangeAt(0);
      const contents = range.extractContents();
      const textNode = document.createTextNode(contents.textContent);
      range.insertNode(textNode);
      
      // Keep selection on the cleaned text
      range.setStartBefore(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
      
      handleContentChange();
    }
  };

  // Handle keyboard events for better RTL support
  const handleKeyDown = (e) => {
    // Handle RTL-specific keyboard shortcuts and behaviors
    if (e.key === 'Enter') {
      // Ensure proper line breaks in RTL context
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          // Ensure cursor is positioned correctly after line break
          if (range.collapsed) {
            const textNode = document.createTextNode('\u200F'); // RTL mark
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.setEndAfter(textNode);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }, 0);
    }
    
    // Handle backspace to maintain proper cursor positioning
    if (e.key === 'Backspace' || e.key === 'Delete') {
      setTimeout(() => handleContentChange(), 0);
    }
  };

  const getTextLength = () => {
    return editorRef.current ? (editorRef.current.textContent || '').length : 0;
  };

  const toolbarButtons = [
    { command: 'bold', icon: FormatBold, tooltip: 'عريض' },
    { command: 'italic', icon: FormatItalic, tooltip: 'مائل' },
    { command: 'underline', icon: FormatUnderlined, tooltip: 'تحته خط' },
    { command: 'justifyRight', icon: FormatAlignRight, tooltip: 'محاذاة يمين' },
    { command: 'justifyCenter', icon: FormatAlignCenter, tooltip: 'محاذاة وسط' },
    { command: 'justifyLeft', icon: FormatAlignLeft, tooltip: 'محاذاة يسار' },
    { command: 'insertUnorderedList', icon: FormatListBulleted, tooltip: 'قائمة نقطية' },
    { command: 'insertOrderedList', icon: FormatListNumbered, tooltip: 'قائمة مرقمة' },
  ];

  return (
    <Grid item xs={12}>
      <Paper elevation={1} sx={{ border: '1px solid', borderColor: 'divider' }}>
        {/* Enhanced Toolbar */}
        <Toolbar 
          variant="dense" 
          sx={{ 
            minHeight: isMobile ? 48 : 56,
            px: 1,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: 0.5,
            direction: 'rtl'
          }}
        >
          {/* Text Formatting */}
          <ButtonGroup size="small" variant="outlined">
            {toolbarButtons.slice(0, 3).map(({ command, icon: Icon, tooltip }) => (
              <Tooltip key={command} title={tooltip}>
                <IconButton
                  size="small"
                  onClick={() => executeCommand(command)}
                  sx={{ minWidth: isMobile ? 32 : 40 }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
          </ButtonGroup>

          {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

          {/* Alignment - RTL order */}
          <ButtonGroup size="small" variant="outlined">
            {toolbarButtons.slice(3, 6).map(({ command, icon: Icon, tooltip }) => (
              <Tooltip key={command} title={tooltip}>
                <IconButton
                  size="small"
                  onClick={() => executeCommand(command)}
                  sx={{ minWidth: isMobile ? 32 : 40 }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
          </ButtonGroup>

          {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

          {/* Lists */}
          <ButtonGroup size="small" variant="outlined">
            {toolbarButtons.slice(6).map(({ command, icon: Icon, tooltip }) => (
              <Tooltip key={command} title={tooltip}>
                <IconButton
                  size="small"
                  onClick={() => executeCommand(command)}
                  sx={{ minWidth: isMobile ? 32 : 40 }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
          </ButtonGroup>

          <Box sx={{ flexGrow: 1 }} />

          {/* Enhanced Action Buttons */}
          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="الألوان والتمييز">
              <IconButton
                size="small"
                onClick={(e) => setColorAnchorEl(e.currentTarget)}
                sx={{ minWidth: isMobile ? 32 : 40 }}
              >
                <Palette fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="القوالب السريعة">
              <IconButton
                size="small"
                onClick={(e) => setTemplateAnchorEl(e.currentTarget)}
                sx={{ minWidth: isMobile ? 32 : 40 }}
              >
                <AutoAwesome fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="رموز الموضة">
              <IconButton
                size="small"
                onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                sx={{ minWidth: isMobile ? 32 : 40 }}
              >
                <EmojiEmotions fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="مسح التنسيق">
              <IconButton
                size="small"
                onClick={clearFormatting}
                sx={{ minWidth: isMobile ? 32 : 40 }}
              >
                <FormatClear fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="تراجع">
              <IconButton
                size="small"
                onClick={handleUndo}
                disabled={undoStack.length === 0}
                sx={{ minWidth: isMobile ? 32 : 40 }}
              >
                <Undo fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="إعادة">
              <IconButton
                size="small"
                onClick={handleRedo}
                disabled={redoStack.length === 0}
                sx={{ minWidth: isMobile ? 32 : 40 }}
              >
                <Redo fontSize="small" />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </Toolbar>

        {/* Quick Format Buttons */}
        <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', direction: 'rtl' }}>
          <ButtonGroup size="small" variant="text">
            <Tooltip title="إضافة معلومات جدول المقاسات">
              <IconButton size="small" onClick={() => insertSpecialFormat('size-chart')}>
                <Straighten fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="إضافة تعليمات العناية">
              <IconButton size="small" onClick={() => insertSpecialFormat('care-instructions')}>
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="إضافة قائمة المميزات">
              <IconButton size="small" onClick={() => insertSpecialFormat('features-list')}>
                <Star fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="إضافة بانر ترويجي">
              <IconButton size="small" onClick={() => insertSpecialFormat('promotion')}>
                <LocalOffer fontSize="small" />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </Box>

        {/* Rich Text Editor */}
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleContentChange}
          onKeyDown={handleKeyDown}
          onPaste={(e) => {
            setTimeout(handleContentChange, 0);
          }}
          onFocus={() => {
            // Ensure proper cursor positioning on focus
            setTimeout(() => {
              if (!window.getSelection().rangeCount) {
                setCursorAtEnd();
              }
            }, 0);
          }}
          sx={{
            minHeight: isMobile ? 150 : 200,
            maxHeight: 400,
            overflow: 'auto',
            p: 2,
            border: 'none',
            outline: 'none',
            fontSize: '14px',
            lineHeight: 1.6,
            fontFamily: theme.typography.fontFamily,
            direction: 'rtl',
            textAlign: 'right',
            unicodeBidi: 'bidi-override', // Better RTL support
            '&:focus': {
              backgroundColor: 'action.hover',
            },
            '& p': {
              margin: '8px 0',
              direction: 'rtl',
            },
            '& ul, & ol': {
              paddingInlineStart: '20px',
              margin: '8px 0',
              direction: 'rtl',
            },
            '& li': {
              margin: '4px 0',
              direction: 'rtl',
            },
            '&:empty::before': {
              content: '"اكتب وصف المنتج هنا... استخدم الأدوات لإضافة تنسيق احترافي 👗✨"',
              color: 'text.secondary',
              fontStyle: 'italic',
              direction: 'rtl',
            }
          }}
        />

        {/* Character Counter */}
        <Box sx={{ 
          p: 1, 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          direction: 'rtl'
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label="جاهز للموضة" size="small" color="primary" variant="outlined" />
            <Chip label="محسن لمحركات البحث" size="small" color="success" variant="outlined" />
          </Box>
          <FormHelperText sx={{ fontSize: '12px', margin: 0 }}>
            {getTextLength()}/{maxLength} حرف
          </FormHelperText>
        </Box>
      </Paper>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorAnchorEl)}
        anchorEl={colorAnchorEl}
        onClose={() => setColorAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 280, direction: 'rtl' }}>
          <Typography variant="subtitle2" gutterBottom>ألوان النص</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
            {COLOR_PALETTE.map((color) => (
              <IconButton
                key={color}
                onClick={() => applyColor(color)}
                sx={{
                  backgroundColor: color,
                  width: 40,
                  height: 40,
                  '&:hover': { opacity: 0.8 }
                }}
              />
            ))}
          </Box>
          <Typography variant="subtitle2" gutterBottom>التمييز</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
            {COLOR_PALETTE.map((color) => (
              <IconButton
                key={`highlight-${color}`}
                onClick={() => highlightText(color)}
                sx={{
                  backgroundColor: color,
                  width: 40,
                  height: 40,
                  opacity: 0.3,
                  '&:hover': { opacity: 0.5 }
                }}
              />
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Template Picker Popover */}
      <Popover
        open={Boolean(templateAnchorEl)}
        anchorEl={templateAnchorEl}
        onClose={() => setTemplateAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 300, maxHeight: 400, overflow: 'auto', direction: 'rtl' }}>
          <Typography variant="subtitle2" gutterBottom>القوالب السريعة</Typography>
          {QUICK_TEMPLATES.map((template) => (
            <Box
              key={template.label}
              sx={{
                p: 1,
                mb: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              onClick={() => insertTemplate(template.text)}
            >
              <Typography variant="body2" fontWeight="bold">{template.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {template.text.substring(0, 50)}...
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Enhanced Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => setEmojiAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ width: isMobile ? 300 : 350, maxHeight: 400, direction: 'rtl' }}>
          {/* Category Tabs */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', p: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Object.keys(EMOJI_CATEGORIES).map((category) => (
                <Chip
                  key={category}
                  label={category}
                  size="small"
                  onClick={() => setSelectedCategory(category)}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Box>

          {/* Emoji Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 0.5,
              p: 1,
              maxHeight: 250,
              overflow: 'auto',
            }}
          >
            {EMOJI_CATEGORIES[selectedCategory].map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                onClick={() => insertEmoji(emoji)}
                sx={{
                  fontSize: '20px',
                  minWidth: 'auto',
                  aspectRatio: '1',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'scale(1.2)',
                  },
                }}
              >
                {emoji}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Popover>
    </Grid>
  );
};

export default ProductDescription;